// Adapted from case-desk src/app/router.ts (commit 1d18e32), 2026-08-18: route-split feature modules
// (das-web § 11) behind the workspace route that owns session, host and tenant context. Every relevant
// view has a stable URL (das-web § 6); the API lives under /api on the same origin and is not routed here.
import { createBrowserRouter, redirect, type RouteObject } from 'react-router';

import { RouteHydrateFallback } from './RouteHydrateFallback';

/**
 * Every route of DAS Web, exported so a test can run the real tree in a memory router. The browser
 * router is only the history it is mounted on.
 */
export const appRoutes: RouteObject[] = [
  {
    path: '/setup/microsoft-365',
    HydrateFallback: RouteHydrateFallback,
    lazy: () => import('../features/m365-setup/M365SetupRoute'),
  },
  {
    path: '/administration',
    HydrateFallback: RouteHydrateFallback,
    lazy: async () => ({ Component: (await import('../features/administration/OperatorRoute')).Component }),
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (await import('../features/administration/OperatorRoute')).Customers,
        }),
      },
      {
        path: 'new',
        lazy: async () => ({
          Component: (await import('../features/administration/OperatorRoute')).NewCustomer,
        }),
      },
      {
        path: 'tenants/:tenantId',
        lazy: async () => ({
          Component: (await import('../features/administration/OperatorRoute')).CustomerDetail,
        }),
      },
      {
        path: 'users',
        lazy: async () => ({ Component: (await import('../features/administration/OperatorRoute')).Users }),
      },
      {
        path: 'users/:requestId',
        lazy: async () => ({
          Component: (await import('../features/administration/OperatorRoute')).UserDetail,
        }),
      },
    ],
  },
  {
    HydrateFallback: RouteHydrateFallback,
    path: '/auth/error',
    lazy: () => import('../features/auth/AuthErrorRoute'),
  },
  {
    HydrateFallback: RouteHydrateFallback,
    path: '/auth/signed-out',
    lazy: () => import('../features/auth/SignedOutRoute'),
  },
  {
    HydrateFallback: RouteHydrateFallback,
    path: '/',
    lazy: () => import('../features/workspace/WorkspaceRoute'),
    children: [
      {
        index: true,
        lazy: () => import('../features/workspace/StartRoute'),
      },
      {
        path: 'notes',
        lazy: () => import('../features/notes/NotesRoute'),
      },
      {
        path: 'notes/new',
        lazy: () => import('../features/notes/NewNoteRoute'),
      },
      {
        path: 'notes/:noteId',
        lazy: () => import('../features/notes/NoteRoute'),
      },
      {
        path: 'pay-transparency',
        lazy: () => import('../features/pay-transparency/PayTransparencyRoute'),
      },
      {
        path: 'pay-transparency/analysis',
        lazy: () => import('../features/pay-transparency/PayTransparencyAnalysisRoute'),
      },
      {
        path: 'pay-transparency/analysis/details',
        lazy: () => import('../features/pay-transparency/PayTransparencyDetailsRoute'),
      },
      {
        path: 'pay-transparency/analysis/groups/:groupId',
        lazy: () => import('../features/pay-transparency/PayTransparencyGroupRoute'),
      },
      {
        path: 'funding',
        lazy: () => import('../features/funding/FundingRoute'),
      },
      {
        path: 'funding/search',
        lazy: () => import('../features/funding/FundingSearchRoute'),
      },
      {
        path: 'funding/programs/:programId',
        lazy: () => import('../features/funding/FundingProgramRoute'),
      },
      {
        path: 'funding/profiles',
        lazy: () => import('../features/funding/FundingProfilesRoute'),
      },
      {
        path: 'funding/profiles/new',
        lazy: () => import('../features/funding/NewFundingProfileRoute'),
      },
      {
        path: 'funding/profiles/:profileId',
        lazy: () => import('../features/funding/EditFundingProfileRoute'),
      },
      {
        // The module opens on its tickets since 24.09.2026: the ticket is the object the work hangs
        // on, and an invoice is what a billing run makes of the ready ones. The query survives, so a
        // bookmarked filter still lands where it pointed.
        path: 'invoice-outgoing',
        loader: ({ request }) => {
          const url = new URL(request.url);
          return redirect(`/invoice-outgoing/tickets${url.search}`);
        },
      },
      {
        // The produced documents, no longer the worklist.
        path: 'invoice-outgoing/invoices',
        lazy: () => import('../features/invoice-outgoing/InvoicesRoute'),
      },
      {
        // Retired 28.09.2026: the ready tickets are billed from the ticket list's "ready for billing"
        // section, by selection and with the invoice date. A bookmark lands there.
        path: 'invoice-outgoing/billing-run',
        loader: () => redirect('/invoice-outgoing/tickets'),
      },
      // The ticket is the object the work hangs on since 24.09.2026; the invoice list stays as the
      // list of produced documents.
      {
        path: 'invoice-outgoing/tickets',
        lazy: () => import('../features/invoice-outgoing/TicketsRoute'),
      },
      {
        path: 'invoice-outgoing/tickets/assess',
        // Retired 28.09.2026: new and changed tickets are read and assessed on the ticket list
        // itself, through "Aktualisieren". A bookmark lands there.
        loader: () => redirect('/invoice-outgoing/tickets'),
      },
      {
        path: 'invoice-outgoing/tickets/:ticketId',
        lazy: () => import('../features/invoice-outgoing/TicketDetailRoute'),
      },
      {
        // Retired 2026-09-24: importing a ticket as a draft invoice, one at a time or in bulk, is the
        // ticket assessment now. A bookmark still lands where that work happens.
        path: 'invoice-outgoing/import/*',
        loader: () => redirect('/invoice-outgoing/tickets'),
      },
      {
        path: 'invoice-outgoing/ai-access',
        lazy: () => import('../features/invoice-outgoing/AiAccessRoute'),
      },
      {
        path: 'invoice-outgoing/import-settings',
        lazy: () => import('../features/invoice-outgoing/ImportSettingsRoute'),
      },
      {
        path: 'invoice-outgoing/escalation',
        lazy: () => import('../features/invoice-outgoing/EscalationRoute'),
      },
      {
        path: 'invoice-outgoing/invoice-mail',
        lazy: () => import('../features/invoice-outgoing/InvoiceMailRoute'),
      },
      {
        path: 'invoice-outgoing/invoices/new',
        lazy: () => import('../features/invoice-outgoing/NewInvoiceRoute'),
      },
      {
        path: 'invoice-outgoing/orders',
        lazy: () => import('../features/invoice-outgoing/OrdersRoute'),
      },
      {
        path: 'invoice-outgoing/importer',
        lazy: () => import('../features/invoice-outgoing/ImporterRoute'),
      },
      {
        path: 'invoice-outgoing/importer/examples',
        lazy: () => import('../features/invoice-outgoing/ImporterExamplesRoute'),
      },
      {
        path: 'invoice-outgoing/customers',
        lazy: () => import('../features/invoice-outgoing/CustomersRoute'),
      },
      {
        path: 'invoice-outgoing/customers/:customerId',
        lazy: () => import('../features/invoice-outgoing/CustomersRoute'),
      },
      {
        // Retired 2026-09-22 (configurable workflows are not pursued). A bookmark still leads
        // somewhere useful: to the worklist, not into the detail route as invoice "automation".
        path: 'invoice-outgoing/automation',
        loader: ({ request }) => {
          const url = new URL(request.url);
          // The query only: a loader's request never carries the URL fragment. Straight to the
          // tickets, the module's entry view since 24.09.2026, rather than through a second redirect.
          return redirect(`/invoice-outgoing/tickets${url.search}`);
        },
      },
      {
        path: 'invoice-outgoing/:invoiceId',
        lazy: () => import('../features/invoice-outgoing/InvoiceDetailRoute'),
      },
      {
        path: 'invoice-outgoing/:invoiceId/edit',
        lazy: () => import('../features/invoice-outgoing/NewInvoiceRoute'),
      },
      {
        path: 'issues',
        lazy: () => import('../features/issues/IssuesRoute'),
      },
      {
        path: 'issues/new',
        lazy: () => import('../features/issues/NewIssueRoute'),
      },
      {
        path: 'issues/:issueId',
        lazy: () => import('../features/issues/IssueRoute'),
      },
      {
        path: 'compliance',
        lazy: () => import('../features/compliance/ComplianceRoute'),
      },
      {
        path: 'admin',
        lazy: () => import('../features/tenant-administration/AdministrationRoute'),
      },
      {
        path: 'admin/company',
        lazy: () => import('../features/tenant-administration/CompanyRoute'),
      },
      {
        path: 'admin/invoice-settings',
        lazy: () => import('../features/tenant-administration/InvoiceSettingsRoute'),
      },
      {
        path: 'admin/mail-processing/:messageId',
        lazy: () => import('../features/invoices/ProcessingRoute'),
      },
      {
        path: 'admin/mail-processing',
        lazy: () => import('../features/invoices/ProcessingRoute'),
      },
      {
        path: 'absence/protocol/:messageId',
        lazy: () => import('../features/absence/ProtocolRoute'),
      },
      {
        path: 'absence/protocol',
        lazy: () => import('../features/absence/ProtocolRoute'),
      },
      {
        path: 'absence/review/:id',
        lazy: () => import('../features/absence/ReviewRoute'),
      },
      {
        path: 'absence/review',
        lazy: () => import('../features/absence/ReviewRoute'),
      },
      {
        path: 'absence/resource-plan',
        lazy: () => import('../features/absence/ResourcePlanRoute'),
      },
      {
        path: 'admin/microsoft-365',
        lazy: () => import('../features/tenant-administration/M365ConnectionRoute'),
      },
      {
        path: 'admin/mail-intakes',
        lazy: () => import('../features/tenant-administration/MailIntakesRoute'),
      },
      {
        path: 'invoices/processing',
        lazy: () => import('../features/tenant-administration/LegacyProcessingRedirect'),
      },
      {
        path: 'invoices',
        lazy: () => import('../features/invoices/InvoicesRoute'),
      },
      {
        path: 'invoices/settings',
        lazy: () => import('../features/tenant-administration/LegacyInvoiceSettingsRedirect'),
      },
      {
        path: 'invoices/:invoiceId',
        lazy: () => import('../features/invoices/InvoiceRoute'),
      },
    ],
  },
  {
    HydrateFallback: RouteHydrateFallback,
    path: '*',
    lazy: () => import('../features/not-found/NotFoundRoute'),
  },
];

export function createAppRouter() {
  return createBrowserRouter(appRoutes);
}
